import { graphql } from '@/axios'
import { fetchOuDetailsQuery } from '@/graphql/ouDetails'
import { findClosestPublicOU } from '@/graphql'

function runQuery (state, resourceType, query) {
  return graphql(query(
    state.selected.rootQueryType,
    state.selected.id,
    state.config[resourceType].perPage,
    state.config[resourceType].page,
    state.config[resourceType].sortField,
    state.config[resourceType].sortOrder,
    state.filter[resourceType].filter)
  )
}

export const loadAsyncData = ({state, commit}, resourceType) => {
  commit('IS_LOADING', true)
  let fetchData = []
  if (Object.keys(state.data).includes(resourceType)) {
    let resource = state.resourceTypes.filter(resource => resource.type === resourceType)[0]
    fetchData = [
      runQuery(state, resource.type, resource.query)
    ]
  } else {
    state.resourceTypes.forEach(resource => {
      fetchData.push(runQuery(state, resource.type, resource.query))
    })
    /* these promise is pushed to fetchData in order to control the 'loading state' attribute */
    if (state.selected.rootQueryType === 'organisationalUnit' && state.selected.id != null) {
      fetchData.push(
        graphql(fetchOuDetailsQuery(Number(state.selected.id)))
        .then((response) => {
          commit('SET_LABEL', response.data.data.organisationalUnit.stringRep)
          commit('SET_OU_DETAILS', response.data.data.organisationalUnit)
        })
      )
    } else if (state.selected.rootQueryType === 'person' && state.selected.id != null) {
      fetchData.push(
        graphql(state.leurisProfile.query({fisPersid: state.selected.id}))
        .then((response) => {
          commit('SET_LABEL', response.data.data.person.name)
          commit('SET_LEURIS_PROFILE', response.data.data.person)
        })
      )
    }
  }

  return Promise.all(fetchData).then(responses => {
    responses.forEach(response => {
      Object.keys(state.data).forEach(resourceType => {
        if (response) {
          let data = response.data.data[state.selected.rootQueryType]
          if (Object.keys(data).includes(resourceType)) {
            commit('SET_DATA', {
              resourceType,
              data: data[resourceType].entries
            })
            commit('SET_TOTAL', {
              resourceType,
              total: data[resourceType].meta.totalEntries
            })
          }
        }
      })

      commit('IS_LOADING', false)
    })
  })
}

export const loadAsyncStaff = ({state, commit}, { startsWith, filter }) => {
  commit('IS_LOADING_STAFF', true)
  graphql(state.staff.query({ startsWith, filter })).then(response => {
    let letterCount = response.data.data.staff.meta.decompositions[0].letterCount
    let totalEntries = response.data.data.staff.meta.totalEntries
    let data = response.data.data.staff.entries

    commit('SET_STAFF_LETTER_COUNT', letterCount)
    commit('SET_STAFF_FULLNAME', filter.fullName)
    commit('SET_STAFF_TOTAL_ENTRIES', totalEntries)
    commit('SET_STAFF_STARTSWITH', startsWith)
    commit('SET_STAFF_DATA', data)
    commit('IS_LOADING_STAFF', false)
  })
}

export const setStaffFullName = ({state, commit}, fullName) => {
  commit('SET_STAFF_FULLNAME', fullName)
}

export const setShowOrgaTree = ({state, commit}, show) => {
  commit('SET_SHOW_ORGATREE', show)
}

export const setConfig = ({state, commit}, { resourceType, property, value }) => {
  commit('SET_CONFIG', { resourceType, property, value })
}

export const setResourceType = ({ state, commit }, type) => {
  commit('SET_RESOURCE_TYPE', type)
}

export const changePage = ({ state, commit }, { resourceType, page }) => {
  commit('CHANGE_PAGE', { resourceType, page })
}

export const setActiveTab = ({state, commit}, activeTab) => {
  commit('SET_ACTIVE_TAB', activeTab)
  commit('SET_RESOURCE_TYPE', state.resourceTypes[activeTab].type)
}

export const setData = ({ state, commit }, { resourceType, data }) => {
  commit('SET_DATA', { resourceType, data })
}

export const changePerPage = ({ state, commit }, { resourceType, perPage }) => {
  commit('CHANGE_PER_PAGE', { resourceType, perPage })
  // show first on perPage change
  commit('SET_CONFIG', { resourceType, property: 'page', value: 1 })
}

export const setSortField = ({ state, commit }, { resourceType, sortField }) => {
  commit('SET_SORT_FIELD', { resourceType, sortField })
  // show first on perPage change
  commit('SET_CONFIG', { resourceType, property: 'page', value: 1 })
}

export const setSortOrder = ({ state, commit }, { resourceType, sortOrder }) => {
  commit('SET_SORT_ORDER', { resourceType, sortOrder })
  // show first on perPage change
  commit('SET_CONFIG', { resourceType, property: 'page', value: 1 })
}

export const setSelected = ({state, commit}, selected) => {
  if (selected.type === 'ou') {
    let redirect = false
    const fromOU = state.selected.id
    let toOU = selected.id || '1'

    if (fromOU !== toOU) {
      // Using `await` does not work at all, sadly. It transforms the whole
      // block into some weird Promise and toOU ends up being undefined
      // I don't even...
      return graphql(findClosestPublicOU(toOU))
        .then(response => {
          let closestPublicOU = response.data.data.closestPublicOrganisationalUnit.id
          if (closestPublicOU !== toOU) {
            console.warn(`Requested OU ${toOU} is not public, redirecting to ${closestPublicOU}`)
            redirect = true
          }
          selected.id = closestPublicOU
          commit('SET_SELECTED', selected)
          return { redirect, orgaID: closestPublicOU }
        })
    } else {
      commit('SET_SELECTED', selected)
      return { redirect, orgaID: toOU }
    }
  } else {
    commit('SET_SELECTED', selected)
  }
}

export const setTotal = ({ state, commit }, { resourceType, total }) => {
  commit('SET_TOTAL', { resourceType, total })
}

export const updateFilter = ({ state, commit }, { resourceType, updateFilter }) => {
  if ('inProject' in updateFilter) {
    if (updateFilter.inProject === 'true' || updateFilter.inProject === null) {
      updateFilter.inProject = true
    } else {
      updateFilter.inProject = false
    }
  }
  if ('fundedByCategory' in updateFilter && updateFilter.fundedByCategory === 'null') {
    updateFilter.fundedByCategory = null
  }
  commit('UPDATE_FILTER', { resourceType, updateFilter })
}

export const updateListSchemaFields = ({ state, commit }, resourceType, updatedListSchemaFields) => {
  commit('UPDATE_LIST_SCHEMA_FIELDS', { resourceType, updatedListSchemaFields })
}

export const setOrganisationalUnitName = ({ state, commit }, name) => {
  commit('SET_ORGANISATIONAL_UNIT_NAME', name)
}

export const isLoading = ({state, commit}, loadingState) => commit('IS_LOADING', loadingState)

export const reset = ({state, commit}) => commit('RESET')



// WEBPACK FOOTER //
// ./src/store/modules/publicView/actions.js